import { useTranslation } from "react-i18next";
import { SetStateAction, useEffect, useState } from "react";
import { OrderStatus, Severity, WeightUnit } from "node-library/dist/constants";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { FrontendOrder } from "node-library/dist/portal";
import { RowDropdown } from "../../../../common-components/inputs";
import { ModalWithButtons } from "../../../../common-components/modals/modal";
import { GreenButton } from "../../../../common-components/button";
import {
	CustomDatePicker,
	CustomDateTimePicker,
} from "../../../../common-components/datePicker";
import { sendPOSTRequest } from "../../../../requestHelper/sendRequest";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { isValid } from "date-fns";
import { ButtonTextLoadingAnimation } from "../../../../common-components/animation/ButtonLoadingAnimation";
import {
	getStatusDescriptionText,
	getStatusString,
} from "../../../../helpers/orderHelper";

interface Props {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	orderId: string;
	orderStatus: OrderStatus;
	setOrderStatus: React.Dispatch<React.SetStateAction<OrderStatus>>;

	completeTime: Date | undefined;
	setCompleteTime: React.Dispatch<React.SetStateAction<Date | undefined>>;
}

export default function EditOrderStateTimeModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { orderId, orderStatus, completeTime, open, setOpen } = props;

	const [currentAdminOrderStatus, setCurrentAdminOrderStatus] =
		useState<OrderStatus>(orderStatus);

	const [completeDate, setCompleteDate] = useState<Date | null>(
		completeTime || null
	);

	//validation

	const [completeTimeError, setCompleteTimeError] = useState<string>("");

	const [loading, setLoading] = useState<boolean>(false);

	function changeAdminOrderStatus(status: OrderStatus) {
		setCurrentAdminOrderStatus(status);
		switch (status) {
			case OrderStatus.IN_PROGRESS:
				setCompleteDate(null);
				break;
			case OrderStatus.COMPLETE:
				setCompleteDate(new Date());
				break;
		}
	}

	const statusSelectionDropdown: React.ReactNode = (
		<RowDropdown
			id="order-status-selection-dropdown"
			className="order-status-selection-dropdown"
			label={translation("OrderStatus")}
			options={Object.values(OrderStatus).map((value) => {
				return {
					value: value,
					title: getStatusString(value, translation),
				};
			})}
			value={currentAdminOrderStatus}
			setValue={(status) => {
				changeAdminOrderStatus(status);
			}}
		></RowDropdown>
	);

	const fieldsForDates: Array<any> = [
		{
			id: "order-complete-date-picker",
			label: `${translation("CompletedOn")}`,
			value: completeDate,
			setValue: setCompleteDate,
			dateOnly: true,
			disabled: currentAdminOrderStatus !== OrderStatus.COMPLETE,
			errorMsg: completeTimeError,
			setErrorMsg: setCompleteTimeError,
		},
	];

	async function handleSubmit() {
		for (const field of fieldsForDates) {
			if (field.value && !isValid(field.value)) {
				field.setErrorMsg(translation("InvalidDate"));
				return;
			} else {
				field.setErrorMsg("");
			}
		}
		try {
			setLoading(true);
			const data = await sendPOSTRequest("/orders/update-order-status-time", {
				orderId: orderId,
				adminOrderStatus: currentAdminOrderStatus,
				completeTime: completeDate,
			});

			props.setOrderStatus(currentAdminOrderStatus);

			// props.setRegisterTime(packageReceivedTime || undefined);
			props.setCompleteTime(completeDate || undefined);

			openSnackbar(
				translation("OrderStatusAndTimeUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			setOpen(false);
		} catch (err: any) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		if (open) {
			setCurrentAdminOrderStatus(orderStatus);
			setCompleteDate(completeTime || null);
		}
	}, [open]);

	const timePickers = fieldsForDates.map((field, index) => {
		return (
			<div key={index} className="modal-datepicker-row">
				<div className="typography-body-01">{field.label}</div>
				{field.dateOnly ? (
					<CustomDatePicker
						selectedDate={field.value}
						disabled={field.disabled}
						setSelectedDate={field.setValue}
						errorMsg={field.errorMsg}
					/>
				) : (
					<CustomDateTimePicker
						selectedDate={field.value}
						disabled={field.disabled}
						setSelectedDate={field.setValue}
						errorMsg={field.errorMsg}
					></CustomDateTimePicker>
				)}
			</div>
		);
	});
	const updateStatusTimeModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={translation("OrderStatusAndTime")}
			headerDescription={translation("OrderStatusAndTimeDescription")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
						margin: "0px 20px",
					}}
				>
					<GreenButton
						key="update-order-status-time-button"
						id="update-order-status-time-button"
						size="flex"
						children={
							loading ? (
								<ButtonTextLoadingAnimation
									text={translation("Loading")}
								></ButtonTextLoadingAnimation>
							) : (
								translation("Update")
							)
						}
						disabled={loading}
						onClick={handleSubmit}
					></GreenButton>
				</div>,
			]}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
				{statusSelectionDropdown}
				{timePickers}
			</div>
		</ModalWithButtons>
	);
	return updateStatusTimeModal;
}
